import React, { useCallback, useEffect, useState, useRef } from "react";
import Swal from 'sweetalert2';
import { Select, Tooltip, message, Modal, Input } from "antd";
import { fetchWithAuth } from "../../utils/api";
import PropTypes from "prop-types";

export default function RegisterMasterTypes({ typeCategory }) {

    const [sessionUserData, setsessionUserData] = useState({});
    const [assetTypesData, setAssetTypesData] = useState([]);
    const [deptsData, setDeptsData] = useState([]);
    const [addTypeLoading, setAddTypeLoading] = useState(false);
    const [selectedAddDeptId, setSelectedAddDeptId] = useState(null);
    const [typeName, setTypeName] = useState("");
    const [isPeripheral, setIsPeripheral] = useState(false);
    const [sessionModuleId, setSessionModuleId] = useState(null);
    const [sessionActionIds, setSessionActionIds] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [typeSearchQuery, setTypeSearchQuery] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedEditType, setSelectedEditType] = useState(null);
    const [editTypeName, setEditTypeName] = useState("");
    const [editLoading, setEditLoading] = useState(false);
    const [isFlowChart, setIsFlowChart] = useState(false);

    const { Option } = Select;
    const offcanvasModalRef = useRef(null);

    useEffect(() => {
        const userDataString = sessionStorage.getItem("userData");
        const storedModule = JSON.parse(localStorage.getItem("ModuleData"));
        const moduleId = storedModule?.Id?.toString();
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            setsessionUserData(userData);
            setSessionModuleId(moduleId);
        }
    }, []);

    useEffect(() => {
        try {
            const rawMenu = sessionStorage.getItem("menuData");
            const parsedMenu = JSON.parse(rawMenu || "[]");

            // Ensure we have a valid number for comparison
            const modId = Number(sessionModuleId);
            let targetName = '';

            if (modId === 14) {
                targetName = 'Assets';
            } else if (modId === 15) {
                targetName = 'Documents';
            }

            if (targetName) {
                // Use .trim() and .toLowerCase() for a safer match
                const checkMenu = parsedMenu.find(item =>
                    item.MenuName?.trim().toLowerCase() === targetName.toLowerCase()
                );

                if (checkMenu?.ActionsIds) {
                    const actionIdArray = checkMenu.ActionsIds.split(",").map(Number);
                    setSessionActionIds(actionIdArray);
                }
            } else {
                console.warn("No targetName matched for sessionModuleId:", modId);
            }
        } catch (err) {
            console.error("Error parsing menuData:", err);
        }
    }, [typeCategory, sessionModuleId]); // Added sessionModuleId as a dependency

    const fetchDDLData = async () => {
        try {
            const sessionDDL = sessionStorage.getItem("masterTypesDdlData");

            if (sessionDDL) {
                const parsed = JSON.parse(sessionDDL);

                setDeptsData(parsed.depts || []);
                return;
            }

            const response = await fetchWithAuth(
                `ADMINRoutes/CWIGetDDLItems?OrgId=${sessionUserData?.OrgId}&UserId=0`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) throw new Error("Network response was not ok");

            const data = await response.json();

            const deptsFilteredData = data.ResultData.filter(
                (item) => item.DDLName === "Departments"
            );

            setDeptsData(deptsFilteredData || []);

            sessionStorage.setItem(
                "masterTypesDdlData",
                JSON.stringify({
                    depts: deptsFilteredData,
                })
            );

        } catch (error) {
            console.error("Failed to fetch DDL data:", error);
            setDeptsData([]);
        }
    };

    const fetchMasterTypes = useCallback(async () => {
        setDataLoading(true);
        try {
            const response = await fetchWithAuth(
                `Portal/GetMasterTypes?OrgId=${sessionUserData?.OrgId}&DeptId=${(typeCategory === 2 || (typeCategory === 3 && sessionModuleId == 15)) ? 0 : selectedAddDeptId}&ModuleId=${sessionModuleId}&TypeCategory=${typeCategory}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) throw new Error("Network response was not ok");

            const data = await response.json();

            setAssetTypesData(data.ResultData || []);
            setDataLoading(false);

        } catch (error) {
            console.error("Failed to fetch DDL data:", error);
            setAssetTypesData([]);
            setDataLoading(false);
        }
    }, [sessionUserData, selectedAddDeptId, sessionModuleId, typeCategory]);

    useEffect(() => {
        if (sessionUserData?.OrgId) {
            fetchMasterTypes();
        }
    }, [fetchMasterTypes, sessionUserData?.OrgId, typeCategory]);

    useEffect(() => {
        if (sessionUserData?.OrgId) {
            fetchDDLData();
        }
    }, [sessionUserData]);

    useEffect(() => {
        if (deptsData && sessionUserData?.DeptId) {
            setSelectedAddDeptId(sessionUserData?.DeptId);
        }
    }, [sessionUserData, deptsData]);

    const handleAddTypeSubmit = async (e) => {
        e.preventDefault();

        if (!selectedAddDeptId) {
            Swal.fire({
                title: "warning",
                text: "Department field is mandatory.",
                icon: "warning",
            });
            setAddTypeLoading(false);
            return;
        }
        if (typeName.trim() === "") {
            Swal.fire({
                title: "warning",
                text: "Type name field is mandatory.",
                icon: "warning",
            });
            setAddTypeLoading(false);
            return;
        }
        setAddTypeLoading(true);

        const payload = {
            TypeCategory: typeCategory,
            TypeName: typeName,
            OrgId: sessionUserData.OrgId,
            CreatedBy: sessionUserData?.Id,
            DeptId: (typeCategory === 2 || (typeCategory === 3 && sessionModuleId == 15))
                ? 0
                : selectedAddDeptId,
            ModuleId: sessionModuleId,
            DirectAssign: isPeripheral ? 1 : 0,
            IsFlowChart: isFlowChart ? 1 : 0,
        }

        try {
            const response = await fetchWithAuth(`Portal/AddMasterTypes`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            setAddTypeLoading(false);

            if (result.ResultData.Status === "Success") {
                Swal.fire({
                    title: "Success",
                    text: `${typeLabelMap[typeCategory]} Type has been saved successfully.`,
                    icon: "success",
                }).then(() =>
                    setTypeName(""),
                    fetchMasterTypes()
                    // setSelectedAddDeptId(null),
                );
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Something went wrong, please try after sometime.",
                    icon: "error",
                });
            }
        } catch (error) {
            console.error("Error during submission:", error.message);
            Swal.fire({
                title: "Error",
                text: "An unexpected error occurred.",
                icon: "error",
            });
        } finally {
            setAddTypeLoading(false);
        }
    };

    const handleClear = () => {
        setSelectedAddDeptId(null);
        setTypeName("");
        // setAssetTypesData([]);
        // setSelectedGetDeptId(null);
    };

    const handleDeleteMasterType = async (item) => {
        Swal.fire({
            title: "Are you sure?",
            text: `Do you want to delete ${typeLabelMap[typeCategory]} type?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `<i class="bi bi-check2-all text-white me-1"></i>Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x-lg text-white me-1"></i>Cancel`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const payload = {
                        Id: item.Id,
                        UpdatedBy: sessionUserData?.Id,
                        Type: typeCategory,
                    };

                    const response = await fetchWithAuth(`Portal/InActiveMasterTypes`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                    });

                    const result = await response.json();

                    if (result.ResultData[0].ResponseCode === 200) {
                        // ✅ 500 = Success in your case
                        fetchMasterTypes();
                        message.success(`${typeLabelMap[typeCategory]} type has been deleted successfully.`);
                    } else if (result.ResultData[0].ResponseCode === 500) {
                        // ⚠️ 200 = Not Deleted / Warning
                        Swal.fire({
                            icon: 'warning',
                            title: 'Not Deleted',
                            text: result.ResultData[0].Message || "This record cannot be deleted at this time.",
                            confirmButtonColor: '#ffc107', // Warning Yellow
                        });
                    } else {
                        // ❌ Other Error codes
                        const errorData = await response.json();
                        message.error(errorData.ResultData[0]?.Message || "An unexpected error occurred.");
                    }
                } catch (error) {
                    console.error("Error during type delete:", error.message);
                    Swal.fire("Error!", "An unexpected error occurred.", "error");
                }
            }
        });
    };

    const filteredTypes = assetTypesData?.filter(type =>
        type.TypeName?.toLowerCase().includes(typeSearchQuery.toLowerCase())
    ) || [];

    const handleOpenEditModal = (type) => {
        setSelectedEditType(type);
        setEditTypeName(type?.TypeName || "");
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedEditType(null);
        setEditTypeName("");
    };

    const handleUpdateMasterType = async () => {
        if (!editTypeName.trim()) {
            Swal.fire({
                title: "Warning",
                text: "Type name is mandatory.",
                icon: "warning",
            });
            return;
        }

        try {
            setEditLoading(true);

            const payload = {
                Id: selectedEditType?.Id,
                TypeName: editTypeName.trim(),
                UserId: sessionUserData?.Id,
            };

            const response = await fetchWithAuth(`EDM/UpdateMasterTypes`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (result?.ResultData?.Status === "Success") {
                Swal.fire({
                    title: "Success",
                    text: "Type updated successfully.",
                    icon: "success",
                });

                handleCloseEditModal();
                fetchMasterTypes();
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Something went wrong, please try again.",
                    icon: "error",
                });
            }
        } catch (error) {
            console.error("Update type error:", error);
            Swal.fire({
                title: "Error",
                text: "An unexpected error occurred.",
                icon: "error",
            });
        } finally {
            setEditLoading(false);
        }
    };

    const typeLabelMap = {
        1: "Asset",
        2: "Document",
        3: "Alert",
    };

    const isDeleteDisabled = sessionActionIds?.includes(29) ? true : false;
    const isEditDocType = sessionActionIds?.includes(34) ? true : false;

    return (
        <div
            className="offcanvas offcanvas-end"
            tabIndex="-1"
            id="offcanvasRightAddMasterTypes"
            aria-labelledby="offcanvasRightLabel"
            style={{ width: "90%" }}
            ref={offcanvasModalRef}
        >
            <style>
                {`
                    @media (min-width: 768px) { /* Medium devices and up (md) */
                        #offcanvasRightAddMasterTypes {
                            width: 55% !important;
                        }
                    }
                `}
            </style>
            <div>
                <div className="offcanvas-header d-flex justify-content-between align-items-center mb-2">
                    <h5 id="offcanvasRightLabel" className="mb-0">{typeLabelMap[typeCategory] || "Type"} Types</h5>
                    <div className="d-flex align-items-center">
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="offcanvas"
                            aria-label="Close"
                        ></button>
                    </div>
                </div>
                <div className="offcanvas-body" style={{ marginTop: "-2rem", maxHeight: "calc(100vh - 4rem)", overflowY: "auto" }}>
                    <div className="card">
                        <div className="p-5">
                            <div className="row g-3 align-items-end mb-3">
                                {/* Department Select */}
                                {!(typeCategory === 2 || (typeCategory === 3 && sessionModuleId == 15)) && (
                                    <div className="col-12 col-md-4">
                                        <label className="form-label fw-bold text-gray-700">Select Department<spn className="text-danger fw-bold">*</spn></label>
                                        <Select
                                            placeholder="Select Department"
                                            showSearch
                                            filterOption={(input, option) =>
                                                option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                            style={{ height: '2.8rem', width: '100%' }}
                                            className="w-100"
                                            value={selectedAddDeptId || undefined}
                                            onChange={(value) => setSelectedAddDeptId(value)}
                                            disabled={addTypeLoading || sessionUserData?.RoleID === 3}
                                        >
                                            {deptsData?.map((dep) => {
                                                const isUserDept = dep.ItemId === sessionUserData?.DeptId;

                                                return (
                                                    <Option key={dep.ItemId} value={dep.ItemId}>
                                                        <div className="d-flex justify-content-between align-items-center w-100">
                                                            <span className={isUserDept ? "fw-bolder text-primary" : ""}>
                                                                {dep.ItemValue}
                                                            </span>
                                                            {isUserDept && (
                                                                <span
                                                                    className="badge badge-light-primary fw-bold"
                                                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                                                >
                                                                    MY DEPT
                                                                </span>
                                                            )}
                                                        </div>
                                                    </Option>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                )}

                                {/* Type Name Input */}
                                <div className="row g-4 align-items-end">

                                    <div className={typeCategory === 2 ? "col-lg-8 col-md-7" : "col-12"}>
                                        <label className="form-label fw-bold text-gray-700">
                                            {typeLabelMap[typeCategory] || "Type"} Name
                                            <span className="text-danger ms-1">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control form-control-sm shadow-sm"
                                            placeholder={`Enter ${typeLabelMap[typeCategory] || "Type"} Name`}
                                            style={{ height: "40px" }}
                                            value={typeName}
                                            onChange={(e) => setTypeName(e.target.value)}
                                            disabled={addTypeLoading}
                                            required
                                        />
                                    </div>

                                    {Number(typeCategory) === 2 && (
                                        <div className="col-lg-4 col-md-5">

                                            <div
                                                className="border rounded-3 px-3 py-2 d-flex justify-content-between align-items-center h-100"
                                                style={{
                                                    minHeight: "46px",
                                                    background: "#f8f9fc"
                                                }}
                                            >
                                                <div>
                                                    <div className="fw-semibold">
                                                        Flow Chart
                                                    </div>
                                                    <small className="text-muted">
                                                        Enable flow chart for this type
                                                    </small>
                                                </div>

                                                <div className="form-check form-switch m-0">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={isFlowChart}
                                                        onChange={(e) => setIsFlowChart(e.target.checked)}
                                                        disabled={addTypeLoading}
                                                        style={{
                                                            width: "3rem",
                                                            height: "1.5rem",
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                        </div>
                                    )}

                                </div>

                                {sessionModuleId === '14' && typeCategory === 1 && (
                                    <div className="col-12 col-md-3">
                                        <div className="d-flex align-items-center mb-2" style={{ height: '2.8rem' }}>
                                            <div className="form-check form-switch form-check-custom form-check-solid">
                                                <input
                                                    className="form-check-input h-25px w-45px cursor-pointer"
                                                    type="checkbox"
                                                    id="peripheralCheck"
                                                    checked={isPeripheral}
                                                    onChange={(e) => setIsPeripheral(e.target.checked)}
                                                    disabled={addTypeLoading}
                                                />
                                                <label className="form-check-label fw-bold text-gray-700 ms-3 d-flex align-items-center" htmlFor="peripheralCheck">
                                                    Direct Assign?
                                                    <Tooltip
                                                        title="Assets of this type will be directly assigned to technicians automatically upon ticket approval."
                                                        placement="top"
                                                        color="#3E97FF"
                                                    >
                                                        <span className="ms-2 text-muted opacity-75 cursor-help">
                                                            <i className="bi bi-info-circle fs-7 text-primary"></i>
                                                        </span>
                                                    </Tooltip>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm me-2"
                                    onClick={handleClear}
                                    disabled={addTypeLoading}
                                >
                                    <i className="fa-solid fa-broom"></i>Clear
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleAddTypeSubmit}
                                    disabled={addTypeLoading}
                                >
                                    <i className="bi bi-bookmark-check"></i>{addTypeLoading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mb-4">
                                <h5 className="fw-bold mb-0" style={{ color: "#1e293b" }}>
                                    Existing {typeLabelMap[typeCategory]} Types
                                    <span className="badge bg-light-primary text-primary ms-2 fs-9 px-2">
                                        {filteredTypes.length} Total
                                    </span>
                                </h5>

                                <div className="position-relative w-100 w-md-250px">
                                    <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted"></i>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid ps-10 border-0 bg-light"
                                        placeholder="Search types..."
                                        style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                                        value={typeSearchQuery}
                                        onChange={(e) => setTypeSearchQuery(e.target.value)}
                                    />
                                    {typeSearchQuery && (
                                        <button
                                            className="btn btn-flush position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                                            onClick={() => setTypeSearchQuery("")}
                                            type="button"
                                        >
                                            <i className="bi bi-x-circle-fill text-muted fs-7"></i>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {dataLoading && (
                                <div className="d-flex justify-content-center py-5">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                    <span className="ms-3 text-muted fs-7">Updating list...</span>
                                </div>
                            )}

                            <div className="table-responsive" style={{ overflowX: 'hidden' }}>
                                <table className="table align-middle table-row-dashed fs-7 gy-2">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-8 text-uppercase gs-0">
                                            <th className="w-50px text-center">#</th>
                                            <th className="min-w-150px">Type Name</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600">
                                        {filteredTypes.length > 0 ? (
                                            filteredTypes.map((type, indx) => {
                                                const isExpiryAlertType = type.DirectAssign === true && sessionModuleId == 15;

                                                return (
                                                    <tr
                                                        key={type.Id || indx}
                                                        className={`transition-3ms ${isExpiryAlertType ? "bg-light-warning" : "hover-bg-light"}`}
                                                    >
                                                        <td className="text-gray-400 text-center">{indx + 1}</td>

                                                        <td>
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                <span
                                                                    className="text-gray-800 fw-bold d-inline-block text-truncate"
                                                                    style={{ maxWidth: "150px" }}
                                                                    title={type.TypeName}
                                                                >
                                                                    {type.TypeName}
                                                                </span>

                                                                {isExpiryAlertType && (
                                                                    <span className="badge badge-light-warning text-warning border border-warning-subtle">
                                                                        <i className="bi bi-bell-fill me-1"></i>
                                                                        Expiry Alert
                                                                    </span>
                                                                )}

                                                                {type.IsFlowChart === true && (
                                                                    <span className="badge badge-light-primary text-primary border border-primary-subtle">
                                                                        <i className="bi bi-diagram-3-fill me-1 text-primary"></i>
                                                                        Flow Chart
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center gap-2">
                                                                {sessionModuleId == 15 && (typeCategory === 2) && isEditDocType && (
                                                                    <button
                                                                        className="btn btn-icon btn-light-primary btn-sm border-0 shadow-sm rounded-circle"
                                                                        style={{
                                                                            width: "32px",
                                                                            height: "32px",
                                                                        }}
                                                                        onClick={() => handleOpenEditModal(type)}
                                                                    >
                                                                        <i className="bi bi-pencil-square fs-6"></i>
                                                                    </button>
                                                                )}

                                                                <button
                                                                    className="btn btn-icon btn-light-danger btn-sm border-0 shadow-sm rounded-circle"
                                                                    style={{
                                                                        width: "32px",
                                                                        height: "32px",
                                                                        cursor: !isDeleteDisabled ? "not-allowed" : "pointer",
                                                                        opacity: !isDeleteDisabled ? 0.4 : 1
                                                                    }}
                                                                    onClick={() => isDeleteDisabled && handleDeleteMasterType(type)}
                                                                    disabled={!isDeleteDisabled}
                                                                >
                                                                    <i className="bi bi-trash3 fs-6"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center py-10">
                                                    <div className="text-muted fs-7">No types found matching your search.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary"
                            style={{ width: "34px", height: "34px" }}
                        >
                            <i className="bi bi-pencil-square"></i>
                        </span>
                        <span className="fw-bold">Edit Type</span>
                    </div>
                }
                open={isEditModalOpen}
                onCancel={handleCloseEditModal}
                onOk={handleUpdateMasterType}
                okText="Update"
                confirmLoading={editLoading}
                destroyOnHidden
                zIndex={2000}
                getContainer={() => offcanvasModalRef.current || document.body}
                maskClosable={false}
            >
                <div className="mt-2">
                    <label className="form-label fw-semibold mb-2 d-flex align-items-center gap-2">
                        {/* <i className="bi bi-tag text-primary"></i> */}
                        <span>Type Name</span>
                    </label>

                    <Input
                        autoFocus
                        value={editTypeName}
                        onChange={(e) => setEditTypeName(e.target.value)}
                        placeholder="Enter type name"
                        maxLength={100}
                        prefix={<i className="bi bi-input-cursor-text text-muted"></i>}
                    />
                </div>
            </Modal>

        </div>
    );
}

RegisterMasterTypes.propTypes = {
    typeCategory: PropTypes.number.isRequired,
};